import { DidResolver } from "@atproto/identity";
import { EnterFullScreenIcon, ExitFullScreenIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { ShuffleIcon } from "lucide-react";
import { useRef } from "react";
import { Editor } from "tldraw";

import { BlueskyIcon } from "@/components/BlueskyIcon";
import { CanvasEditor } from "@/components/canvas-editor/CanvasEditor";
import { createHead } from "@/components/createHead";
import { Description } from "@/components/Description";
import { FeedEmbed } from "@/components/FeedEmbed";
import { FeedPulseCard } from "@/components/FeedPulseCard";
import { useFullscreen } from "@/components/hooks/useFullscreen";
import { MarketplaceBody } from "@/components/MarketplacePage";
import { FeedAvatar } from "@/components/ui/avatar";
import { ButtonAnchor, ButtonLink } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { UserAnalyticsSummary } from "@/components/UserAnalyticsSummary";
import { getFeedPulseQueryOptions, getPublicFeedQueryOptions } from "@/lib/api";
import { PublicFeed } from "@/lib/types";
import { atUriToBskyUrl, cn } from "@/lib/utils";

export const Route = createFileRoute("/_marketplace-layout/feeds/$feedId/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const feed = await queryClient.ensureQueryData(
      getPublicFeedQueryOptions(parseInt(feedId)),
    );

    if (!feed) {
      throw redirect({ to: "/feeds" });
    }

    const [, userDid] = feed.algorithm_uri.match(/(did:[^/]*)\//) || [];

    if (!userDid) {
      throw new Error("User not found");
    }

    const resolver = new DidResolver({});
    const handle = await resolver
      .resolve(userDid)
      .then((r) => r?.alsoKnownAs?.[0]!.replace("at://", ""));

    // The Feed Pulse is optional decoration: a slow or failing pulse endpoint
    // must never delay or break the page, so prefetch without awaiting the
    // result's success. null (no pulse) is a valid, cached answer.
    await queryClient
      .prefetchQuery(getFeedPulseQueryOptions(parseInt(feedId)))
      .catch(() => undefined);

    return {
      feed: feed as Omit<PublicFeed, "draft_algorithm_manifest" | "settings">,
      handle,
    };
  },
  head: ({ loaderData }) => {
    const feed = loaderData?.feed as PublicFeed;

    return createHead({
      title: `${feed.display_name} Bluesky Feed | Graze`,
      ogTitle: feed.display_name,
      description: feed.description,
      pathname: `/feeds/${feed.id}`,
      ogImage: `/feeds/$feedId/og.png`,
      ogParams: { feedId: feed.id },
    });
  },
});

function RouteComponent() {
  const loaderData = Route.useLoaderData();
  const feed = loaderData?.feed as PublicFeed;
  const handle = loaderData?.handle as string;
  const { data: pulse } = useQuery(getFeedPulseQueryOptions(feed.id));
  const editorRef = useRef<Editor>(undefined);
  const [fullscreen, setFullscreen] = useFullscreen({ editorRef });
  const mobileSquare =
    "border md:border-0 shadow-md md:shadow-none border-slate-7 dark:border-slatedark-7 md:m-0 rounded-lg md:rounded-none";

  return (
    <MarketplaceBody>
      <div className="pt-4 md:pt-20 md:pb-8 flex flex-col gap-4 md:gap-8 items-center">
        <FeedAvatar feed={feed} className="w-16 h-16 md:w-24 md:h-24" />

        <h1 className="text-5xl md:text-8xl font-bold instrument-serif text-center">
          {feed.display_name}
        </h1>
        <div className="flex flex-col gap-0.5 md:gap-1 flex-1">
          <Link
            className="text-lg md:text-xl text-slate-dim"
            to="/user/$userId"
            params={{ userId: feed.user_id.toString() }}
          >
            @{handle}
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <SimpleTooltip message="Open on Bluesky">
            <ButtonAnchor
              variant="ghost"
              size="smIcon"
              href={atUriToBskyUrl(feed.algorithm_uri)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">View algorithm</span>
              <BlueskyIcon className="h-4 w-4" />
            </ButtonAnchor>
          </SimpleTooltip>
          <ButtonLink
            to="/app/create"
            search={{ remix: feed.id }}
            variant="outline"
            size="sm"
          >
            <ShuffleIcon />
            Remix
          </ButtonLink>
        </div>
      </div>
      {pulse && (
        <FeedPulseCard pulse={pulse} className="w-full max-w-3xl mx-auto" />
      )}
      <UserAnalyticsSummary
        variant="compact"
        analytics={feed.stats.details}
        summary={feed.stats.summary}
      />
      <Description className="text-lg">{feed.description}</Description>
      <div
        className={cn(
          "overflow-hidden min-h-[60vh] md:border md:rounded-lg border-slate-7 dark:border-slatedark-7",
          "flex flex-col md:flex-row gap-4 md:gap-0",
          "md:flex-1",
        )}
      >
        <div
          className={cn(
            "relative md:flex-1 bg-slate-action",
            fullscreen
              ? "fixed inset-0 z-10 bg-slate-app"
              : ["h-[300px] md:h-auto overflow-hidden", mobileSquare],
          )}
        >
          <CanvasEditor
            ref={editorRef}
            feedId={feed.id}
            feedVersion={feed.draft_algorithm_manifest}
            hideUi={true}
          />

          <Toggle
            className="absolute top-2 right-2"
            aria-label={fullscreen ? "Exit Full Screen" : "Enter Full Screen"}
            size="sm"
            pressed={false}
            onPressedChange={() => {
              setFullscreen(!fullscreen);
            }}
          >
            {fullscreen ? <ExitFullScreenIcon /> : <EnterFullScreenIcon />}
          </Toggle>
        </div>
        {feed.id && (
          <div
            className={cn(
              mobileSquare,
              "flex flex-col bg-slate-subtle",
              "max-h-[60vh] md:max-h-full h-full md:h-auto",
              "md:max-w-[500px] flex-1 overflow-auto md:border-l border-slate-7 dark:border-slatedark-7",
            )}
          >
            <FeedEmbed feedId={`${feed.id}`} fullscreen={false} />
          </div>
        )}
      </div>
    </MarketplaceBody>
  );
}
